import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, filter, take } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { DeviceService } from './device.service';

@Injectable({ providedIn: 'root' })
export class GaTrackingService {
    private isScriptLoadedSubject = new BehaviorSubject<boolean>(false);
    public isReady$: Observable<boolean> = this.isScriptLoadedSubject.asObservable();

    private readonly GA_TRACKING_ID = environment.gaTrackingId;
    private gaScriptElement: HTMLScriptElement | null = null;

    constructor(private deviceService: DeviceService) {}

    public initializeAnalytics(): Observable<void> {
        if (this.gaScriptElement) {
            return of(undefined);
        }

        if (!this.GA_TRACKING_ID) {
            console.warn('GaTrackingService: No se encontro gaTrackingId en el environment. GA no sera inicializado.');
            return throwError(() => new Error('GA Tracking ID no configurado'));
        }

        console.log('GaTrackingService: Iniciando carga asincrona...');

        const windowAny = window as any;
        windowAny.dataLayer = windowAny.dataLayer || [];
        windowAny.gtag = windowAny.gtag || function () {
            windowAny.dataLayer.push(arguments);
        };

        windowAny.gtag('js', new Date());
        // send_page_view en false: la app es una SPA y el propio servicio se encarga
        // de emitir el page_view en cada cambio de ruta (ver trackPageView / NavigationEnd).
        windowAny.gtag('config', this.GA_TRACKING_ID, { send_page_view: false });

        const script = document.createElement('script');
        script.src = `https://www.googletagmanager.com/gtag/js?id=${this.GA_TRACKING_ID}`;
        script.async = true;
        script.id = 'google-analytics-script';

        this.gaScriptElement = script;

        return new Observable<void>(subscriber => {
            document.head.appendChild(script);

            const loadHandler = () => {
                console.log('GaTrackingService: Script de Google Analytics cargado exitosamente.');
                this.isScriptLoadedSubject.next(true);
                subscriber.next();
                subscriber.complete();
            };

            const errorHandler = (err: any) => {
                console.error('GaTrackingService Error: Fallo de carga GA.', err);
                this.isScriptLoadedSubject.next(false);
                subscriber.error('Failed to load GA script');
            };

            script.addEventListener('load', loadHandler);
            script.addEventListener('error', errorHandler);

            return () => {
                script.remove();
                if (document.head.contains(script)) {
                    document.head.removeChild(script);
                }
                script.removeEventListener('load', loadHandler);
                script.removeEventListener('error', errorHandler);
            };
        }).pipe(
            catchError((err) => {
                console.warn('GaTrackingService: No se pudo inicializar GA por error de carga. Ejecutando con tracking deshabilitado.', err);
                this.isScriptLoadedSubject.next(false);
                return throwError(() => new Error('GA Initialization Failed'));
            })
        );
    }

    private safeTrack(action: string, label: string, value: number | string): void {
        // Usamos RxJS para esperar reactivamente a que isReady$ emita 'true' (el Gatekeeper).
        this.isReady$.pipe(
            filter(isReady => isReady),
            take(1)
        ).subscribe(() => {

            const gtagFunction = (window as any).gtag;

            if (!gtagFunction || typeof gtagFunction !== 'function') {
                console.error('GA Tracking Fatal: Error de ejecucion - Funcion gtag no disponible.', this.gaScriptElement);
                return;
            }

            // Disparar el comando GA (el corazon del rastreo)
            gtagFunction('event', action, {
                'event_category': label,
                'event_label': value
            });
            console.log(`GA Event Dispatchado: ${action} | Label: ${label}`);
        });
    }

    public trackEvent(action: string, label: string, value: number | string): void {
        this.safeTrack(action, label, value);
    }

    /**
     * Envia un page_view para soportar navegacion SPA (Angular Router).
     * Usa los parametros estandar de GA4 (page_title / page_location) en vez del formato
     * generico de safeTrack, para que la vista aparezca correctamente en el reporte
     * "Paginas y pantallas". Se envia la URL de la pagina como page_title, tal como se
     * pidio, para poder identificar cada vista en los reportes.
     */
    public trackPageView(pageUrl: string): void {
        this.isReady$.pipe(
            filter(isReady => isReady),
            take(1)
        ).subscribe(() => {

            const gtagFunction = (window as any).gtag;

            if (!gtagFunction || typeof gtagFunction !== 'function') {
                console.error('GA Tracking Fatal: Error de ejecucion - Funcion gtag no disponible.', this.gaScriptElement);
                return;
            }

            gtagFunction('event', 'page_view', {
                'page_title': pageUrl,
                'page_location': window.location.href,
                'page_path': pageUrl,
                'device_type': this.deviceService.getDeviceType()
            });
            console.log(`GA PageView Dispatchado: ${pageUrl}`);
        });
    }

    /**
     * Evento estandar de login. Se dispara cuando el usuario inicia sesion correctamente.
     */
    public trackLogin(username: string): void {
        this.safeTrack('login', 'Autenticacion de usuario', username);
    }
}
